/* Supplement Guide Generator
 * Client-side PDF via jsPDF (no backend PDF service), same approach as Cymbiotika.
 * Product data comes from `supplement_guide_entry` metaobjects rendered by the section.
 *
 * PDF layout (Figma 981:7821 "Inno Personalized Guide"):
 *   A4 portrait, #f6f6f6 page, 3 pre-made blocks per page,
 *   block box 539×241pt at x=28, y = 30 + i × (241 + 30).
 */
(() => {
  'use strict';

  const PDF = {
    pageBg: [246, 246, 246],
    marginX: 28,
    marginTop: 30,
    gap: 30,
    blockW: 539,
    blockH: 241,
    bottomMargin: 20, // minimum space left under the last box on a page
    maxImgPx: 1617, // 3× the block width
    maxBgPx: 1785, // 3× the page width
    jpegQuality: 0.9,
  };
  const JSPDF_CDN = 'https://cdn.jsdelivr.net/npm/jspdf@4.2.1/dist/jspdf.umd.min.js';

  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[™®©]/g, '')
      .replace(/[^a-z0-9+]+/g, ' ')
      .trim();

  // Saved guide (localStorage). Stored as { items, savedAt } and dropped once it's older than
  // store.ttlHours since the last change (section setting "Keep the guide for"); 0 = keep forever.
  const store = {
    ttlHours: 24,
    get(key) {
      try {
        const v = JSON.parse(window.localStorage.getItem(key) || '[]');
        if (Array.isArray(v)) return v; // saved before expiry existed: keep until the next change
        if (!v || !Array.isArray(v.items)) return [];
        if (this.ttlHours > 0 && Date.now() - (v.savedAt || 0) > this.ttlHours * 3600000) {
          window.localStorage.removeItem(key);
          return [];
        }
        return v.items;
      } catch (e) {
        return [];
      }
    },
    set(key, val) {
      try {
        window.localStorage.setItem(key, JSON.stringify({ items: val, savedAt: Date.now() }));
      } catch (e) {
        /* storage unavailable (private mode) — selection just won't persist */
      }
    },
  };

  let jsPDFPromise;
  function loadJsPDF(src) {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
    if (jsPDFPromise) return jsPDFPromise;
    const inject = (url) =>
      new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = url;
        s.async = true;
        s.onload = () => (window.jspdf && window.jspdf.jsPDF ? resolve(window.jspdf.jsPDF) : reject(new Error('jsPDF missing')));
        s.onerror = () => reject(new Error('Failed to load ' + url));
        document.head.appendChild(s);
      });
    jsPDFPromise = (src ? inject(src).catch(() => inject(JSPDF_CDN)) : inject(JSPDF_CDN)).catch((e) => {
      jsPDFPromise = null;
      throw e;
    });
    return jsPDFPromise;
  }

  // Fetch an image (Shopify CDN sends CORS headers) and re-encode as JPEG so
  // jsPDF never has to deal with WebP/AVIF/transparency.
  async function toJpeg(url, maxPx = PDF.maxImgPx) {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('Image ' + res.status + ': ' + url);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('Could not decode ' + url));
        i.src = objectUrl;
      });
      const scale = Math.min(1, maxPx / img.naturalWidth);
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'rgb(' + PDF.pageBg.join(',') + ')';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      return { data: canvas.toDataURL('image/jpeg', PDF.jpegQuality), w, h };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  function absUrl(u) {
    if (!u) return u;
    return u.startsWith('//') ? window.location.protocol + u : u;
  }

  class SupplementGuide {
    constructor(root) {
      this.root = root;
      this.key = root.dataset.storageKey || 'inno-supplement-guide';
      const hours = parseFloat(root.dataset.storageHours);
      if (Number.isFinite(hours) && hours >= 0) store.ttlHours = hours;
      this.grid = root.querySelector('[data-sg-grid]');
      this.cards = Array.from(root.querySelectorAll('[data-sg-card]'));
      // Product entries (what goes in the guide/PDF) vs stacks (cards that add several entries).
      this.byId = new Map(this.cards.filter((c) => !('stack' in c.dataset)).map((c) => [c.dataset.id, c]));
      this.allById = new Map(this.cards.map((c) => [c.dataset.id, c]));
      this.stacks = new Map();
      this.cards
        .filter((c) => 'stack' in c.dataset)
        .forEach((c) => {
          const members = [...new Set((c.dataset.members || '').split('|').filter((h) => this.byId.has(h)))];
          if (members.length) this.stacks.set(c.dataset.id, members);
          else c.remove(); // every product in this stack is unpublished — hide it
        });
      this.cards = this.cards.filter((c) => c.isConnected);
      this.guide = root.querySelector('[data-sg-guide]');
      this.list = root.querySelector('[data-sg-list]');
      this.scroller = root.querySelector('[data-sg-scroller]');
      this.moreBtn = root.querySelector('[data-sg-more]');
      this.tpl = root.querySelector('[data-sg-item-template]');
      this.generateBtn = root.querySelector('[data-sg-generate]');
      this.generateLabel = root.querySelector('[data-sg-generate-label]');
      this.errorEl = root.querySelector('[data-sg-error]');
      this.modal = root.querySelector('[data-sg-modal]');
      // Themes like Debut put a transform on the page wrapper, which makes position:fixed
      // act like position:absolute for anything inside it. Move the dialog to <body>
      // (in a .sg wrapper so it keeps the styles) so it always centres in the viewport.
      if (this.modal && this.modal.parentElement !== document.body) {
        const portal = document.createElement('div');
        portal.className = 'sg sg-portal';
        portal.appendChild(this.modal);
        document.body.appendChild(portal);
        this.portal = portal;
      }
      this.scrim = root.querySelector('[data-sg-scrim]');
      this.drawerToggle = root.querySelector('[data-sg-drawer-toggle]');
      this.countEl = root.querySelector('[data-sg-count]');
      this.searchInput = root.querySelector('[data-sg-search]');
      this.noResults = root.querySelector('[data-sg-noresults]');
      this.catsEl = root.querySelector('[data-sg-cats]');

      this.query = '';
      this.category = '';
      this.doc = null;
      // Keep only ids that still exist (entries can be unpublished).
      this.selected = store.get(this.key).filter((id) => this.byId.has(id));

      this.sortCards();
      this.buildFilter();
      const fromUrl = this.readUrlSelection();
      if (!fromUrl) this.prefillFromOrder();
      this.bind();
      this.render();
      if (this.pendingTrack) this.track(...this.pendingTrack);
      if (fromUrl) this.autoGenerate(fromUrl);
    }

    // Pre-filled guide links: /pages/supplement-guide?products=inno-shred-glp-1,night-shred-glp-1
    // Each value can be a guide entry handle, a Shopify product handle (optionally handle:variant-title,
    // e.g. inno-fast:stimulant-free; extra options joined with ~), or a stack handle
    // (a stack adds all its products). The list replaces the shopper's saved selection.
    // Or by name, one &item= per product: ?item=Inno Fast™ | Stimulant-Free&item=Java Shred™ (see matchTitle).
    // The PDF is built straight away; add &generate=0 to only fill the guide.
    readUrlSelection() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return null;
      }
      const raw = params.getAll('products').concat(params.getAll('product'));
      // items=Name | Variant~Name 2~... (one parameter, because email click-tracking keeps only the last of
      // repeated parameters); item=... may also be repeated for hand-made links.
      const items = params.getAll('items').join('~').split('~').concat(params.getAll('item')).map((x) => x.trim()).filter(Boolean);
      if (!raw.length && !items.length) return null;
      const tokens = raw.join(',').split(/[\s,|]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);

      // Store product handle → guide cards. Uses the entry's Product field, else the handle in its Product URL.
      const byProduct = new Map();
      this.cards.forEach((c) => {
        let h = (c.dataset.product || '').toLowerCase();
        if (!h) {
          const m = (c.dataset.url || '').match(/\/products\/([^/?#]+)/i);
          h = m ? decodeURIComponent(m[1]).toLowerCase() : '';
        }
        if (!h) return;
        if (!byProduct.has(h)) byProduct.set(h, []);
        byProduct.get(h).push(c.dataset.id);
      });
      const compact = (x) => norm(x).replace(/ /g, '');
      // "inno-fast:stimulant-free" → the entry for that product whose name includes the variant
      // (e.g. "Inno Fast™ - Stimulant Free"); otherwise the product's plain entry (shortest name).
      const fromProduct = (handle, variant) => {
        const ids = byProduct.get(handle);
        if (!ids) return null;
        if (ids.length === 1) return ids[0];
        // variant options arrive as "opt1~opt2" (e.g. stimulant-free~60-capsules)
        const opts = (variant || '').split('~').map(compact).filter((o) => o.length > 2);
        const title = (id) => compact(this.allById.get(id).dataset.title);
        return ids.find((id) => opts.some((o) => title(id).includes(o))) ||
          ids.slice().sort((a, b) => title(a).length - title(b).length)[0];
      };
      const ids = [];
      const missing = [];
      const add = (id) => { if (!ids.includes(id)) ids.push(id); };
      tokens.forEach((token) => {
        const [t, variant] = token.split(':');
        const id = this.byId.has(t) ? t
          : this.stacks.has('stack:' + t) ? 'stack:' + t
          : this.stacks.has(t) ? t
          : fromProduct(t, variant);
        if (!id) return missing.push(token);
        if (this.stacks.has(id)) this.stacks.get(id).forEach(add);
        else add(id);
      });
      // By name: &items=Product Title | Variant Title~Next Product (one entry per order line).
      items.forEach((item) => {
        const id = this.matchTitle(item);
        if (!id) return missing.push(item);
        if (this.stacks.has(id)) this.stacks.get(id).forEach(add);
        else add(id);
      });
      if (missing.length) console.warn('[supplement-guide] not found in the guide:', missing.join(', '));

      // Drop the parameters so a refresh doesn't overwrite later changes the shopper makes.
      params.delete('products');
      params.delete('item');
      params.delete('items');
      params.delete('product');
      const generate = params.get('generate');
      params.delete('generate');
      try {
        const q = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
      } catch (e) { /* ignore */ }

      if (!ids.length) return null;
      this.pendingTrack = ['open_link', { sg_link_products: ids.length, sg_link_missing: missing.length }];
      this.selected = ids;
      store.set(this.key, this.selected);
      return { generate: !/^(0|false|no)$/i.test(generate || '') };
    }

    // Match an order line by name to a guide product or stack.
    // "Inno Fast™ | Stimulant-Free" → "Inno Fast™ - Stimulant Free"; "Inno Shred™ | With Stimulants / 60 Capsules" → "Inno Shred™".
    // A guide name matches when it equals the store name or is the start of it (whole words), so landing-page
    // copies like "Inno Shred™ GLP-1 - Special Offer" still find "Inno Shred™ GLP-1". The longest match wins.
    matchTitle(item) {
      const key = (x) => norm(String(x || '').replace(/&/g, ' + ')).replace(/\+/g, ' plus ').replace(/\s+/g, ' ').trim();
      // "3 Bottles of HMB+", "2-Pack Inno Shred", "6 x Volcarn" → the product name alone
      const unpack = (x) => String(x || '')
        .replace(/^\s*(buy\s+)?\d+\s*(-|x)?\s*(bottles?|packs?|bags?|tubs?|jars?|boxes?|pouches?|units?|cans?|count|ct|month(s)?(\s+supply)?)?\s*(of\s+)?/i, '');
      if (!this.titleIndex) {
        this.titleIndex = this.cards.map((c) => [key(c.dataset.title), c.dataset.id]).filter(([k]) => k);
      }
      const [product, variant = ''] = String(item).split('|');
      const base = key(unpack(product)) || key(product);
      if (!base) return null;
      const opts = variant.split('/').map(key).filter((o) => o && o !== 'default title');
      const candidates = [base].concat(opts.map((o) => base + ' ' + o));
      // Compare without spaces ("Beetroot" = "Beet Root"), cutting the store name only between words.
      const compact = (x) => x.replace(/ /g, '');
      if (!this.titleCompact) this.titleCompact = new Map(this.titleIndex.map(([k, id]) => [compact(k), id]));
      let best = null;
      candidates.forEach((name) => {
        const words = name.split(' ');
        let prefix = '';
        words.forEach((w) => {
          prefix += w;
          const id = this.titleCompact.get(prefix);
          if (id && (!best || prefix.length > best.len)) best = { id, len: prefix.length };
        });
      });
      return best && best.id;
    }

    // Account page: start an empty guide with the products from the customer's latest order
    // (data-prefill = "Name | Variant~Name 2~…"). Only when nothing is saved yet; never builds the PDF by itself.
    prefillFromOrder() {
      const raw = (this.root.dataset.prefill || '').trim();
      if (!raw || this.selected.length) return;
      const seenKey = this.key + ':prefilled';
      if (store.get(seenKey).includes(raw)) return; // already offered this order once — respect a cleared guide
      const ids = [];
      raw.split('~').forEach((item) => {
        const id = this.matchTitle(item.trim());
        if (!id) return;
        (this.stacks.has(id) ? this.stacks.get(id) : [id]).forEach((h) => { if (!ids.includes(h)) ids.push(h); });
      });
      store.set(seenKey, [raw]);
      if (!ids.length) return;
      this.selected = ids;
      store.set(this.key, this.selected);
      this.pendingTrack = ['prefill_from_order', { sg_link_products: ids.length }];
    }

    autoGenerate(opts) {
      if (window.matchMedia && window.matchMedia('(max-width: 989px)').matches) this.openDrawer(true);
      if (opts.generate) this.generate();
    }

    sortCards() {
      const sorted = this.cards.slice().sort((a, b) => {
        const d = (parseInt(a.dataset.sort, 10) || 9999) - (parseInt(b.dataset.sort, 10) || 9999);
        return d || a.dataset.title.localeCompare(b.dataset.title);
      });
      const frag = document.createDocumentFragment();
      sorted.forEach((c) => {
        const text = norm(c.dataset.title + ' ' + (c.dataset.keywords || ''));
        // also index a compact copy so "glp1" matches "GLP-1" and "nitrowood" matches "Nitro Wood"
        c._search = text + ' ' + text.replace(/ /g, '');
        frag.appendChild(c);
      });
      this.grid.appendChild(frag);
      this.cards = sorted;
    }

    readCategories() {
      const el = this.root.querySelector('[data-sg-categories]');
      let raw = [];
      try {
        raw = JSON.parse((el && el.textContent) || '[]');
      } catch (e) {
        console.warn('[supplement-guide] bad category JSON', e);
      }
      // Keep only entries that are on the page (drafts / deleted entries drop out),
      // dedupe, and hide categories with nothing in them.
      return raw
        .map((c) => ({
          name: String(c.name || '').trim(),
          sort: Number.isFinite(+c.sort) ? +c.sort : 9999,
          // stacks listed on the category show first, then its products
          entries: [
            ...new Set(
              (c.stacks || []).map((h) => 'stack:' + h).filter((id) => this.stacks.has(id))
                .concat((c.entries || []).filter((h) => this.byId.has(h))),
            ),
          ],
        }))
        .filter((c) => c.name && c.entries.length)
        .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
    }

    buildFilter() {
      const cats = this.readCategories();
      // Automatic "Stacks" filter listing every stack (merged into a category of the same name if one exists).
      const label = (this.root.dataset.stacksLabel || '').trim();
      if (label && this.stacks.size) {
        const stackIds = this.cards.filter((c) => this.stacks.has(c.dataset.id)).map((c) => c.dataset.id);
        const same = cats.find((c) => c.name.toLowerCase() === label.toLowerCase());
        if (same) same.entries = [...new Set(stackIds.concat(same.entries))];
        else cats.unshift({ name: label, sort: -1, entries: stackIds });
      }
      this.categories = new Map(cats.map((c) => [c.name, c.entries]));
      this.catsEl.innerHTML = '';
      if (!this.categories.size) {
        this.catsEl.hidden = true;
        return;
      }
      ['', ...this.categories.keys()].forEach((cat) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sg__cat';
        b.dataset.value = cat;
        b.textContent = cat || 'All';
        this.catsEl.appendChild(b);
      });
      this.catsEl.hidden = false;
      this.syncFilterUI();
    }

    syncFilterUI() {
      this.catsEl.querySelectorAll('.sg__cat').forEach((b) => {
        const on = b.dataset.value === this.category;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
    }

    openDrawer(open) {
      this.guide.classList.toggle('is-open', open);
      this.drawerToggle.setAttribute('aria-expanded', String(open));
      this.scrim.hidden = !open;
      if (open) this.updateScrollHint();
    }

    // Mobile drawer: show the "more →" button and edge fade while the list can scroll further right.
    updateScrollHint() {
      cancelAnimationFrame(this.hintFrame);
      this.hintFrame = requestAnimationFrame(() => {
        const l = this.list;
        const scrollable = l.scrollWidth > l.clientWidth + 2;
        const atEnd = l.scrollLeft + l.clientWidth >= l.scrollWidth - 4;
        const show = scrollable && !atEnd;
        this.scroller.classList.toggle('has-more', show);
        this.moreBtn.hidden = !show;
      });
    }

    bind() {
      this.grid.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sg-toggle]');
        if (!btn) return;
        const id = btn.closest('[data-sg-card]').dataset.id;
        if (this.stacks.has(id)) this.toggleStack(id);
        else this.toggle(id);
      });

      this.list.addEventListener('click', (e) => {
        const btn = e.target.closest('.sg-guide__remove');
        if (btn) this.toggle(btn.closest('[data-id]').dataset.id);
      });
      this.list.addEventListener('scroll', () => this.updateScrollHint(), { passive: true });
      window.addEventListener('resize', () => this.updateScrollHint());
      this.moreBtn.addEventListener('click', () => {
        const l = this.list;
        l.scrollBy({ left: Math.max(80, l.clientWidth * 0.75), behavior: 'smooth' });
      });

      this.root.querySelectorAll('[data-sg-clear]').forEach((b) =>
        b.addEventListener('click', () => {
          const had = this.selected.length;
          this.selected = [];
          this.commit();
          this.track('clear', { sg_cleared: had });
        }),
      );

      let t;
      this.searchInput.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          this.query = norm(this.searchInput.value);
          clearTimeout(this.searchTrackTimer);
          if (this.query.length >= 2) {
            const q = this.searchInput.value.trim();
            this.searchTrackTimer = setTimeout(() => this.track('search', { sg_search_term: q }), 1500);
          }
          // Searching always looks through everything: drop back to "All".
          if (this.query && this.category) {
            this.category = '';
            this.syncFilterUI();
            this.catsEl.scrollTo({ left: 0, behavior: 'smooth' });
          }
          this.applyFilters();
        }, 120);
      });

      this.catsEl.addEventListener('click', (e) => {
        const b = e.target.closest('.sg__cat');
        if (!b || b.dataset.value === this.category) return;
        this.category = b.dataset.value;
        this.syncFilterUI();
        this.applyFilters();
        this.track('category', { sg_category: this.category || 'All' });
        // keep the chosen pill in view when the row scrolls sideways (mobile)
        const row = this.catsEl;
        if (row.scrollWidth > row.clientWidth) {
          const left = b.offsetLeft - row.offsetLeft - (row.clientWidth - b.offsetWidth) / 2;
          row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      });

      this.drawerToggle.addEventListener('click', () => this.openDrawer(!this.guide.classList.contains('is-open')));
      this.scrim.addEventListener('click', () => this.openDrawer(false));

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!this.modal.hidden) this.closeModal();
        else if (this.guide.classList.contains('is-open')) this.openDrawer(false);
      });

      this.generateBtn.addEventListener('click', () => this.generate());
      this.modal.querySelector('[data-sg-download]').addEventListener('click', () => this.download());
      this.modal.querySelector('[data-sg-modal-close]').addEventListener('click', () => this.closeModal());
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });

      // Keep tabs in sync.
      window.addEventListener('storage', (e) => {
        if (e.key !== this.key) return;
        this.selected = store.get(this.key).filter((id) => this.byId.has(id));
        this.doc = null;
        this.render();
      });
    }

    // Analytics: pushes supplement_guide_<action> events to the GTM dataLayer (GA4 etc. via GTM).
    track(action, params = {}) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({
          event: 'supplement_guide_' + action,
          sg_location: this.root.classList.contains('sg--embedded') ? 'account' : 'page',
          sg_count: this.selected.length,
        }, params));
      } catch (e) { /* never let tracking break the guide */ }
    }

    toggle(id) {
      const i = this.selected.indexOf(id);
      const adding = i === -1;
      if (adding) this.selected.push(id);
      else this.selected.splice(i, 1);
      this.commit();
      this.track(adding ? 'add' : 'remove', { sg_product: (this.byId.get(id) || {}).dataset?.title || id });
    }

    // A stack adds each of its products to the guide. If they're all already in, it removes them.
    toggleStack(id) {
      const members = this.stacks.get(id) || [];
      const allIn = members.every((h) => this.selected.includes(h));
      if (allIn) this.selected = this.selected.filter((h) => !members.includes(h));
      else members.forEach((h) => { if (!this.selected.includes(h)) this.selected.push(h); });
      this.commit();
      this.track(allIn ? 'remove_stack' : 'add_stack', { sg_product: (this.allById.get(id) || {}).dataset?.title || id });
    }

    commit() {
      store.set(this.key, this.selected);
      this.doc = null; // selection changed → rebuild on next Generate
      this.render();
    }

    applyFilters() {
      const inCat = this.category ? this.categories.get(this.category) || [] : null;
      const inSet = inCat && new Set(inCat);

      // Within a category, use the order the merchant set on the category's entry list;
      // the "All" view uses each entry's own sort order.
      const order = inCat ? inCat.map((h) => this.allById.get(h)).concat(this.cards.filter((c) => !inSet.has(c.dataset.id))) : this.cards;
      if (order.some((c, i) => this.grid.children[i] !== c)) {
        const frag = document.createDocumentFragment();
        order.forEach((c) => frag.appendChild(c));
        this.grid.appendChild(frag);
      }

      let shown = 0;
      this.cards.forEach((c) => {
        const okQ = !this.query || this.query.split(' ').every((w) => c._search.includes(w));
        const okC = !inSet || inSet.has(c.dataset.id);
        c.hidden = !(okQ && okC);
        if (!c.hidden) shown++;
      });
      this.noResults.hidden = shown > 0;
    }

    render() {
      const set = new Set(this.selected);
      this.cards.forEach((c) => {
        const members = this.stacks.get(c.dataset.id);
        const on = members ? members.every((h) => set.has(h)) : set.has(c.dataset.id);
        const btn = c.querySelector('[data-sg-toggle]');
        btn.classList.toggle('is-added', on);
        btn.setAttribute('aria-pressed', String(on));
        c.querySelector('[data-sg-label]').textContent = on ? 'Remove' : 'Add To Guide';
      });

      // Reuse list items that are already showing so their thumbnails don't reload/flash,
      // and only build items for newly added products.
      const existing = new Map(Array.from(this.list.children).map((li) => [li.dataset.id, li]));
      const frag = document.createDocumentFragment();
      this.selected.forEach((id) => {
        let li = existing.get(id);
        if (!li) li = this.buildItem(id);
        frag.appendChild(li);
      });
      this.list.innerHTML = '';
      this.list.appendChild(frag);

      const n = this.selected.length;
      this.guide.dataset.count = String(n);
      this.countEl.textContent = n ? '(' + n + ')' : '';
      this.generateBtn.disabled = n === 0;
      this.errorEl.hidden = true;
      if (!n) this.openDrawer(false);
      this.updateScrollHint();
    }

    // Thumbnail for "In My Guide": reuse the exact file the product card has already
    // downloaded (instant, from the browser cache) instead of requesting a new size.
    thumbSrc(card) {
      const cardImg = card.querySelector('.sg-card__img');
      if (cardImg && cardImg.complete && cardImg.naturalWidth && cardImg.currentSrc) return cardImg.currentSrc;
      return card.dataset.thumb || (cardImg && (cardImg.currentSrc || cardImg.src)) || '';
    }

    buildItem(id) {
      const card = this.byId.get(id);
      const li = this.tpl.content.firstElementChild.cloneNode(true);
      li.dataset.id = id;
      const img = li.querySelector('img');
      img.loading = 'eager';
      img.decoding = 'async';
      // transparent pixel keeps the grey placeholder square when there is no image
      img.src = this.thumbSrc(card) || 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      li.querySelector('.sg-guide__name').textContent = card.dataset.title;
      li.querySelector('.sg-guide__remove').setAttribute('aria-label', 'Remove ' + card.dataset.title);
      return li;
    }

    // PDF order: "PDF priority" on each entry, lowest first (e.g. pre-workout/pre-meal low,
    // anytime in the middle, night high). No priority = middle (50). Ties keep the order added.
    pdfOrder() {
      const MID = 50;
      const pri = (id) => {
        const v = parseFloat(this.byId.get(id).dataset.pdfPriority);
        return Number.isFinite(v) ? v : MID;
      };
      return this.selected
        .map((id, i) => ({ id, i, p: pri(id) }))
        .sort((a, b) => a.p - b.p || a.i - b.i)
        .map((x) => x.id);
    }

    setBusy(busy) {
      this.generateBtn.classList.toggle('is-loading', busy);
      this.generateBtn.disabled = busy || this.selected.length === 0;
      this.generateBtn.setAttribute('aria-busy', String(busy));
      this.generateLabel.textContent = busy ? 'Generating…' : 'Generate Now';
    }

    async generate() {
      if (!this.selected.length || this.busy) return;
      if (this.doc) return this.openModal();
      this.busy = true;
      this.setBusy(true);
      this.errorEl.hidden = true;
      try {
        const ds = this.root.dataset;
        const bg = (u) => (u ? toJpeg(absUrl(u), PDF.maxBgPx) : Promise.resolve(null));
        const [jsPDF, firstBg, pageBg, blocks] = await Promise.all([
          loadJsPDF(ds.jspdfSrc),
          bg(ds.firstBg),
          bg(ds.pageBg),
          Promise.all(this.pdfOrder().map((id) => toJpeg(absUrl(this.byId.get(id).dataset.block)))),
        ]);
        // Each block links to its product page (entry's Product URL field, else the linked product).
        const links = this.pdfOrder().map((id) => {
          const u = (this.byId.get(id).dataset.url || '').trim();
          if (!u) return '';
          try {
            const url = new URL(u, window.location.origin);
            // UTM tags from the section setting; never overwrite tags already on a hand-entered URL.
            const extra = new URLSearchParams((this.root.dataset.linkParams || '').trim().replace(/^\?/, ''));
            if ([...extra.keys()].length) {
              extra.forEach((v, k) => {
                if (!url.searchParams.has(k)) url.searchParams.set(k, v);
              });
              if (!url.searchParams.has('utm_content')) url.searchParams.set('utm_content', id);
            }
            return url.href;
          } catch (e) {
            return '';
          }
        });
        this.doc = this.buildPdf(jsPDF, { firstBg, pageBg }, blocks, links);
        this.openModal();
        this.track('generate', { sg_products: this.pdfOrder().map((id) => this.byId.get(id).dataset.title).join(', ') });
        this.root.dispatchEvent(
          new CustomEvent('supplement-guide:generated', {
            bubbles: true,
            detail: { products: this.pdfOrder().map((id) => this.byId.get(id).dataset.title) },
          }),
        );
      } catch (err) {
        console.error('[supplement-guide]', err);
        this.errorEl.textContent = 'Sorry, we couldn’t create your guide. Please try again.';
        this.errorEl.hidden = false;
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }

    buildPdf(jsPDF, bgs, blocks, links = []) {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      doc.setProperties({ title: 'My Personalized Supplement Guide', author: 'Inno Supps', creator: 'Inno Supps Supplement Guide' });

      const num = (v, d) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : d);
      const firstTop = num(this.root.dataset.firstTop, PDF.marginTop);
      const pageTop = num(this.root.dataset.pageTop, PDF.marginTop);
      // How many 241pt boxes fit below `top`, keeping the bottom margin.
      const fits = (top) => Math.max(1, Math.floor((H - top - PDF.bottomMargin + PDF.gap) / (PDF.blockH + PDF.gap)));

      // Paint a page: background image stretched edge-to-edge (export it at A4 ratio), else flat grey.
      const paint = (img) => {
        doc.setFillColor(PDF.pageBg[0], PDF.pageBg[1], PDF.pageBg[2]);
        doc.rect(0, 0, W, H, 'F');
        if (img) doc.addImage(img.data, 'JPEG', 0, 0, W, H, undefined, 'FAST');
      };

      let i = 0;
      let page = 0;
      while (i < blocks.length) {
        const top = page === 0 ? firstTop : pageTop;
        if (page > 0) doc.addPage();
        paint(page === 0 ? bgs.firstBg || bgs.pageBg : bgs.pageBg);
        const n = fits(top);
        for (let slot = 0; slot < n && i < blocks.length; slot++, i++) {
          const img = blocks[i];
          const x = PDF.marginX;
          const y = top + slot * (PDF.blockH + PDF.gap);
          // contain the image inside the 539×241 slot, centered
          const s = Math.min(PDF.blockW / img.w, PDF.blockH / img.h);
          const w = img.w * s;
          const h = img.h * s;
          doc.addImage(img.data, 'JPEG', x + (PDF.blockW - w) / 2, y + (PDF.blockH - h) / 2, w, h, undefined, 'FAST');
          if (links[i]) doc.link(x + (PDF.blockW - w) / 2, y + (PDF.blockH - h) / 2, w, h, { url: links[i] });
        }
        page++;
      }
      return doc;
    }

    openModal() {
      this.lastFocus = document.activeElement;
      this.modal.hidden = false;
      this.openDrawer(false);
      // Stop the page scrolling behind the dialog, and don't let focus() scroll it either.
      this.prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.modal.querySelector('[data-sg-download]').focus({ preventScroll: true });
    }

    closeModal() {
      this.modal.hidden = true;
      document.documentElement.style.overflow = this.prevOverflow || '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    download() {
      if (!this.doc) return;
      const name = (this.root.dataset.pdfFilename || 'Supplement-Guide').replace(/\.pdf$/i, '') + '.pdf';
      this.doc.save(name);
      this.track('download');
    }
  }

  const init = () =>
    document.querySelectorAll('[data-sg]').forEach((el) => {
      if (!el._sg) el._sg = new SupplementGuide(el);
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target && e.target.querySelector && e.target.querySelector('[data-sg]');
    if (el && el._sg && el._sg.portal) el._sg.portal.remove();
  });
  document.addEventListener('shopify:section:load', init); // theme editor
})();
